import { IconButton } from "@mui/material"
import { PrintOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"

import { formatInventoryLocation } from "../../../utils"
import { AdjustmentHistoryQuantityChangeCell } from "../components/AdjustmentHistoryTable/AdjustmentHistoryQuantityChangeCell"
import { AdjustmentHistoryStatusCell } from "../components/AdjustmentHistoryTable/AdjustmentHistoryStatusCell"
import { ADJUSTMENT_TYPES_WITH_WORK_ORDERS, PALLET_LABEL_PRINTABLE_STATUSES } from "../constants"

import type { AdjustmentHistoryLot, InventoryAdjustmentType } from "@/graphql/codegen/graphql"
import { isNil } from "@/utils/utils"

interface UseAdjustmentHistoryLotsColumnsProps {
  adjustmentType: InventoryAdjustmentType
  isPrinting: boolean
  onPalletLabelPrint: (palletReferenceNumber: number) => void
}

export const useAdjustmentHistoryLotsColumns = ({
  adjustmentType,
  isPrinting,
  onPalletLabelPrint,
}: UseAdjustmentHistoryLotsColumnsProps): ColumnDef<AdjustmentHistoryLot>[] => {
  return [
    { accessorKey: "event", header: "Event", cell: ({ row }) => row.original.event, enableSorting: false },
    {
      accessorKey: "palletNbr",
      header: "Pallet #",
      cell: ({ row }) => row.original.palletNbr ?? "",
      enableSorting: false,
    },
    {
      accessorKey: "location",
      header: "Location",
      cell: ({ row }) => formatInventoryLocation(row.original.location),
      enableSorting: false,
    },
    { accessorKey: "lotNbr", header: "Lot #", cell: ({ row }) => row.original.lotNbr ?? "", enableSorting: false },
    {
      accessorKey: "batchNbr",
      header: "Batch #",
      cell: ({ row }) => row.original.batchNbr ?? "",
      enableSorting: false,
    },
    {
      accessorKey: "qtyChange",
      header: "Qty Change",
      cell: ({ row }) => <AdjustmentHistoryQuantityChangeCell qtyChange={row.original.qtyChange} />,
      enableSorting: false,
    },
    {
      accessorKey: "lotStatus",
      header: "Status",
      cell: ({ row }) =>
        row.original.lotStatus ? <AdjustmentHistoryStatusCell status={row.original.lotStatus} /> : "",
      enableSorting: false,
    },
    ...(ADJUSTMENT_TYPES_WITH_WORK_ORDERS.includes(adjustmentType)
      ? [
          {
            accessorKey: "printQrCode",
            header: "Print QR Code",
            enableSorting: false,
            cell: ({ row }) => {
              const { palletNbr, lotStatus } = row.original
              if (isNil(palletNbr)) return null

              const canPrintQrCode = !isNil(lotStatus) && PALLET_LABEL_PRINTABLE_STATUSES.includes(lotStatus)

              return (
                <IconButton
                  size='small'
                  disabled={isPrinting || !canPrintQrCode}
                  onClick={() => onPalletLabelPrint(palletNbr)}
                  data-testid='adjustment-history.lots-table.print-pallet-label-button'
                >
                  <PrintOutlined />
                </IconButton>
              )
            },
            size: 140,
          } as ColumnDef<AdjustmentHistoryLot>,
        ]
      : []),
  ]
}
